
/* === quick-view-modal.js === */
if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends HTMLElement {
      static MAX_CACHE_SIZE = 10;

      constructor() {
        super();
        this.overlay = this.querySelector('[data-quick-view-overlay]');
        this.container = this.querySelector('[data-quick-view-container]');
        this.content = this.querySelector('[data-quick-view-content]');
        this.closeBtn = this.querySelector('[data-quick-view-close]');
        this.cache = new Map();
        this.isOpen = false;
        this.triggerElement = null;

        this.boundHandleTriggerClick = this.handleTriggerClick.bind(this);
        this.boundHandleKeydown = this.handleKeydown.bind(this);
      }

      connectedCallback() {
        if (!this.container || !this.content) return;

        this.bindEvents();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.boundHandleTriggerClick);
        document.removeEventListener('keydown', this.boundHandleKeydown);
      }

      bindEvents() {
        document.addEventListener('click', this.boundHandleTriggerClick);
        this.closeBtn?.addEventListener('click', () => this.hide());
        this.overlay?.addEventListener('click', () => this.hide());
        document.addEventListener('keydown', this.boundHandleKeydown);
      }

      handleKeydown(e) {
        if (e.key === 'Escape' && this.isOpen) {
          this.hide();
        }
      }

      handleTriggerClick(e) {
        const trigger = e.target.closest('[data-quick-view-trigger]');
        if (!trigger) return;

        e.preventDefault();
        e.stopPropagation();

        const handle = trigger.dataset.productHandle;
        if (!handle) return;

        this.triggerElement = trigger;
        this.show(handle);
      }

      async show(handle) {
        this.setLoading(true);
        this.open();

        try {
          let html = this.cache.get(handle);

          if (!html) {
            html = await this.fetchProduct(handle);

            if (this.cache.size >= QuickAddModal.MAX_CACHE_SIZE) {
              const firstKey = this.cache.keys().next().value;
              this.cache.delete(firstKey);
            }
            this.cache.set(handle, html);
          }

          this.content.innerHTML = html;
          this.setLoading(false);
          this.reinitComponents();
          this.applyFocusTrap();

        } catch {
          const safeHandle = encodeURIComponent(handle);
          this.content.innerHTML = `
            <div class="quick-view__error">
              <p>Unable to load product. Please try again.</p>
              <a href="/products/${safeHandle}" class="button button--secondary">View product page</a>
            </div>
          `;
          this.setLoading(false);
        }
      }

      async fetchProduct(handle) {
        const response = await fetch(`/products/${handle}?section_id=quick-view-product`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        return response.text();
      }

      open() {
        this.classList.add('modal--open');
        this.setAttribute('aria-hidden', 'false');
        document.body.classList.add('modal-open');
        this.isOpen = true;
      }

      applyFocusTrap() {
        try {
          if (typeof trapFocus === 'function') {
            const firstFocusable = this.content.querySelector(
              'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
            );
            trapFocus(this.container, firstFocusable || this.container);
          } else {
            this.container.focus();
          }
        } catch {
          this.container.focus();
        }
      }

      hide(added = false) {
        this.classList.remove('modal--open');
        this.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('modal-open');

        try {
          if (typeof removeTrapFocus === 'function') {
            removeTrapFocus(this.triggerElement);
          } else if (this.triggerElement) {
            this.triggerElement.focus();
          }
        } catch {
          if (this.triggerElement) {
            this.triggerElement.focus();
          }
        }

        this.isOpen = false;

        setTimeout(() => {
          document.body.dispatchEvent(new CustomEvent('modalClosed'));
          this.content.innerHTML = '';
        }, 300);
      }

      reinitComponents() {
        queueMicrotask(() => {
          this.content.querySelectorAll('variant-radios').forEach(variantRadio => {
            if (variantRadio.initializeComponent && !variantRadio.productData) {
              try {
                variantRadio.initializeComponent();
              } catch {
              }
            }
          });
        });

        this.content.querySelectorAll('[data-quantity-button]').forEach(btn => {
          btn.addEventListener('click', (e) => {
            const input = btn.parentElement.querySelector('input[type="number"]');
            if (!input) return;

            const currentValue = parseInt(input.value) || 1;
            const min = parseInt(input.min) || 1;
            const max = parseInt(input.max) || 999;

            if (btn.dataset.quantityButton === 'minus' && currentValue > min) {
              input.value = currentValue - 1;
            } else if (btn.dataset.quantityButton === 'plus' && currentValue < max) {
              input.value = currentValue + 1;
            }

            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });
      }

      setLoading(state) {
        this.classList.toggle('modal--loading', state);
      }

      get opened() {
        return this.isOpen;
      }
    }
  );
}

